import { tryParseUrl } from './url';
import { reusedGroups, isWeakPassword } from './passwordCheckup';
import { isFinancialItem } from './financialDomains';

/**
 * Builds the ordered, **domain-collapsed** rows for the Security Checkup, shared
 * by web and the extension so both order identically.
 *
 * - Flagged logins (exposed / reused / weak) are grouped by host, so a domain
 *   with several saved logins shows as **one row** with a `loginCount` badge.
 * - Each row represents the group's "worst/most-reused" login (its reuse count +
 *   severity drive the row).
 * - Ordering (user decision 2026-10-15): **financial sites first, then by reuse
 *   count (most-reused first), then severity, then name.**
 */

export type CheckupTier = 0 | 1 | 2 | 3; // 0 exposed+reused · 1 exposed · 2 reused · 3 weak

export interface CheckupLoginInput {
  id: string;
  name: string;
  password?: string | null;
  /** The login's web identifiers (URLs or bare domains) — for grouping + the
   *  financial classification. */
  webIdentifiers?: readonly string[];
}

export interface CheckupDomainRow {
  /** Item id of the representative (worst) login on this domain — the Fix target. */
  repId: string;
  /** Representative login's display name. */
  name: string;
  /** Host the group collapsed on (null when no parseable identifier). */
  domain: string | null;
  /** How many flagged logins this domain has (badge when > 1). */
  loginCount: number;
  /** The representative (most-reused) login's reuse count (1 when unique). */
  reuseCount: number;
  tier: CheckupTier;
  financial: boolean;
  /** Factual reason line, derived from the representative's tier + reuse count. */
  reason: string;
}

export interface CheckupRowsResult {
  rows: CheckupDomainRow[];
  /** Strong (not-flagged) passwords, for the "N of M are strong" summary. */
  strongCount: number;
  /** Total checkable passwords. */
  total: number;
  /** Number of domain rows to review. */
  reviewCount: number;
}

function hostOf(identifiers: readonly string[] | undefined): string | null {
  if (!identifiers) return null;
  for (const id of identifiers) {
    const h = tryParseUrl(id)?.hostname?.replace(/^www\./, '').toLowerCase();
    if (h) return h;
  }
  return null;
}

function reasonFor(tier: CheckupTier, reuseCount: number): string {
  switch (tier) {
    case 0:
      return `Found in a data breach · reused across ${reuseCount} logins`;
    case 1:
      return 'Found in a data breach';
    case 2:
      return `Reused across ${reuseCount} logins`;
    default:
      return 'Weak password';
  }
}

/** Order: financial first → most-reused → most-severe → name. */
export function compareCheckupRows(a: CheckupDomainRow, b: CheckupDomainRow): number {
  if (a.financial !== b.financial) return a.financial ? -1 : 1;
  if (a.reuseCount !== b.reuseCount) return b.reuseCount - a.reuseCount;
  if (a.tier !== b.tier) return a.tier - b.tier;
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

export function buildCheckupRows(
  logins: ReadonlyArray<CheckupLoginInput>,
  exposedIds: ReadonlySet<string>,
): CheckupRowsResult {
  const reuseCountById = new Map<string, number>();
  for (const group of reusedGroups(logins.map((l) => ({ id: l.id, password: l.password })))) {
    for (const id of group) reuseCountById.set(id, group.length);
  }
  const weakIds = new Set(logins.filter((l) => isWeakPassword(l.password)).map((l) => l.id));

  interface Flagged { login: CheckupLoginInput; reuseCount: number; tier: CheckupTier }
  const flagged: Flagged[] = [];
  for (const login of logins) {
    const exposed = exposedIds.has(login.id);
    const reuseCount = reuseCountById.get(login.id) ?? 1;
    const reused = reuseCount >= 2;
    const weak = weakIds.has(login.id);
    if (!exposed && !reused && !weak) continue;
    const tier: CheckupTier = exposed && reused ? 0 : exposed ? 1 : reused ? 2 : 3;
    flagged.push({ login, reuseCount, tier });
  }

  const groups = new Map<string, Flagged[]>();
  for (const f of flagged) {
    const host = hostOf(f.login.webIdentifiers);
    const key = host ?? `id:${f.login.id}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(f);
    else groups.set(key, [f]);
  }

  const rows: CheckupDomainRow[] = [];
  for (const [key, members] of groups) {
    // Representative = the most-reused, then most-severe, then by name.
    members.sort(
      (a, b) =>
        b.reuseCount - a.reuseCount ||
        a.tier - b.tier ||
        a.login.name.localeCompare(b.login.name, undefined, { sensitivity: 'base' }),
    );
    const rep = members[0];
    const domain = key.startsWith('id:') ? null : key;
    rows.push({
      repId: rep.login.id,
      name: rep.login.name,
      domain,
      loginCount: members.length,
      reuseCount: rep.reuseCount,
      tier: rep.tier,
      financial: isFinancialItem(rep.login.webIdentifiers ?? []),
      reason: reasonFor(rep.tier, rep.reuseCount),
    });
  }
  rows.sort(compareCheckupRows);

  return {
    rows,
    strongCount: logins.length - flagged.length,
    total: logins.length,
    reviewCount: rows.length,
  };
}
